import React, { useEffect, useMemo, useState } from 'react';
import { PersonRecord } from '../../../../redux/Candidate/Record/Record.type';
import BaseModalComponent, { StyledBaseModal } from '../../BaseModal.component';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '../../../../constants/date.contant';
import styled from 'styled-components';
import SpinnerComponent, {
  StyledSpinnerView,
} from '../../../Spinner.component';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../../hooks/useAppSelector.hook';
import { getEventStatusList } from '../../../../redux/Event/Status/EventStatus.slice';
import { getEventStatusListThunk } from '../../../../redux/Event/Status/EventStatus.thunk';
import { EventStatus } from '../../../../redux/Event/Status/EventStatus.type';
import { uniqueId } from 'lodash';
import RadioComponent from '../../../Input/Radio.component';
import TextareaComponent from '../../../Input/Textarea.component';
import FormLayoutComponent from '../../../Form/FormLayout.component';
import ButtonComponent, { StyledButton } from '../../../Button.component';
import { updatePersonRecordThunk } from '../../../../redux/Candidate/Record/Record.thunk';
import ApiWrapper from '../../../../hooks/useApi.hook';
import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { getCandidatePreviewThunk } from '../../../../redux/Candidate/Candidate.thunk';
import { textAriasMaxLength } from '../../../../constants/textArias.constant';
import { getCandidateSelected } from '../../../../redux/Candidate/Candidate.slice';
import ReplyToCandidateModal from '../../ReplyToCandidateModal.component';
import GoogleCalendarEventModalComponent from '../../Calendar/GoogleCalendarEventModal.component';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';

export interface CandidateResultModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  info: PersonRecord;
}

type FormData = {
  statusId: number;
  comment?: string;
};

const StyledCandidateResultModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;
const StyledCandidateResultModalStatusStack = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, auto);
  padding: 10px 0;
`;
const StyledCandidateResultModalStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
const StyledCandidateResultModalBodyTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.LABEL};
`;

const StyledCandidateResultModal = styled.div`
  ${StyledBaseModal} {
    width: 800px;
  }

  ${StyledSpinnerView} {
    height: 300px;
  }
`;

/**
 * Consider refactoring
 * Open -> Close => 7 renders! 4 on open and 3 on close
 */
const RecordResultModalComponent = ({ info }: CandidateResultModalProps) => {
  const dispatch = useAppDispatch();
  const list = useAppSelector(getEventStatusList);
  const selectedCandidate = useAppSelector(getCandidateSelected);
  const { closeAllModals, closeModal, openModal } = useModalManager();
  const { user } = useAuthHook();
  const { t } = useTranslation();

  const [isValid, setIsValid] = useState(false);

  const defaultData = {
    statusId: 0,
    comment: info.comment,
  };

  const [recordResultData, setRecordResultData] =
    useState<FormData>(defaultData);

  useEffect(() => {
    if (!info) return;
    dispatch(
      getEventStatusListThunk({
        eventId: info.status?.event.id,
        orderOffset: info.status?.order,
      }),
    );
  }, [dispatch, info]);

  const handleChange = (key: keyof FormData, value: number | string) => {
    if (key === 'statusId') {
      setIsValid(true);
    }
    setRecordResultData({
      ...recordResultData,
      [key]: value,
    });
  };

  const titleHeader =
    info.date &&
    `${info.status!.event.name} 
    ${dayjs.unix(info.date).format(DATE_TIME_FORMAT)}`;

  const onSaveResult = async () => {
    await ApiWrapper(
      updatePersonRecordThunk({
        personId: info.personId,
        recordId: info.id,
        date: info.date,
        jobsId: info.jobs?.map(({ id }) => id),
        jobTypeId: info.jobType?.id,
        sourceId: info.source?.id,
        hrId: info.hr?.id,
        recruiterId: info.recruiter?.id,
        techSpecialistId: info.techSpecialist?.id,
        referralId: info.referral?.id,
        regionId: info.region?.id,
        departmentId: info.department?.id,
        salary: info.salary || null,
        salaryBrutto: info.salaryBrutto || null,
        ...recordResultData,
      }),
    );

    const selectedStatus = list.items.find(
      (item: EventStatus) => item.id === recordResultData.statusId,
    );

    closeAllModals();

    if (
      selectedStatus?.hasPlanningTime &&
      selectedStatus?.code &&
      checkUserRequiredPermissions(user.permissions, [
        UserPermissions.CAN_CREATE_GOOGLE_MEET_INTERVIEW,
      ])
    ) {
      const candidate = selectedCandidate.item;
      openModal(GoogleCalendarEventModalComponent, {
        eventStatusCode: selectedStatus.code,
        record: info,
        candidateName: candidate?.fullName,
        candidateEmail: candidate?.email,
        candidateAttachments: candidate?.attachments,
      });
    } else {
      openModal(ReplyToCandidateModal);
    }

    if (!selectedCandidate.item) {
      dispatch(getCandidatePreviewThunk(info.personId));
    }
  };

  const renderFooter = useMemo(() => {
    return (
      <StyledCandidateResultModalFooter>
        <ButtonComponent
          appearance="primary"
          disabled={!isValid}
          handleClick={onSaveResult}
        >
          {t('Next')}
        </ButtonComponent>
        <ButtonComponent handleClick={closeModal}>
          {t('Cancel')}
        </ButtonComponent>
      </StyledCandidateResultModalFooter>
    );
  }, [recordResultData, isValid]);

  const renderContent = useMemo(
    () => (
      <div>
        <FormLayoutComponent>
          <StyledCandidateResultModalBodyTitle>
            {t('Result')}:
          </StyledCandidateResultModalBodyTitle>
          <StyledCandidateResultModalStack>
            <StyledCandidateResultModalStatusStack>
              {list.items.map((item: EventStatus) => (
                <RadioComponent
                  key={uniqueId('event-status-')}
                  selected={recordResultData.statusId === item.id}
                  description={item.name}
                  handleChange={() => handleChange('statusId', item.id)}
                />
              ))}
            </StyledCandidateResultModalStatusStack>
            <StyledCandidateResultModalBodyTitle>
              {t('Comment')}:
            </StyledCandidateResultModalBodyTitle>
            <TextareaComponent
              maxLength={textAriasMaxLength.EXTENDED}
              value={recordResultData.comment}
              onChange={(value: string) => handleChange('comment', value)}
            />
          </StyledCandidateResultModalStack>
        </FormLayoutComponent>
      </div>
    ),
    [list, recordResultData],
  );

  return (
    <StyledCandidateResultModal>
      <BaseModalComponent header={titleHeader} footer={renderFooter}>
        {!list || !list.loading ? renderContent : <SpinnerComponent />}
      </BaseModalComponent>
    </StyledCandidateResultModal>
  );
};

export default RecordResultModalComponent;
